import { useMemo, useState } from 'react';
import { App as AntApp, Button, Card, Form, Input, InputNumber, Modal, Popconfirm, Segmented, Select, Space, Tag, Table, Typography } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  useCashCategories, useCreateCashCategory, useUpdateCashCategory, useDeleteCashCategory,
  type CashCategory, type CashCategoryScope,
} from '../api/cash';
import { asApiError } from '@/shared/lib/api';

const SCOPES: CashCategoryScope[] = ['income', 'expense', 'both'];

export function CashCategoriesPage() {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const { data, isLoading } = useCashCategories();
  const create = useCreateCashCategory();
  const update = useUpdateCashCategory();
  const remove = useDeleteCashCategory();
  const [editing, setEditing] = useState<CashCategory | null>(null);
  const [open, setOpen] = useState(false);
  const [scopeFilter, setScopeFilter] = useState<'all' | 'income' | 'expense'>('all');
  const [form] = Form.useForm();

  const filtered = useMemo(() => {
    const list = data ?? [];
    if (scopeFilter === 'all') return list;
    // A "both" category applies to either direction, so it shows in both filters.
    return list.filter((c) => c.scope === scopeFilter || c.scope === 'both');
  }, [data, scopeFilter]);

  function openCreate() { setEditing(null); form.resetFields(); setOpen(true); }
  function openEdit(c: CashCategory) {
    setEditing(c);
    form.setFieldsValue({ name: c.name, scope: c.scope, requiresCounterparty: c.requiresCounterparty, sortOrder: c.sortOrder, active: c.active });
    setOpen(true);
  }

  async function onOk() {
    try {
      const v = await form.validateFields();
      if (editing) {
        await update.mutateAsync({ id: editing.id, input: v });
        void message.success(t('kassa.categoryUpdated'));
      } else {
        await create.mutateAsync(v);
        void message.success(t('kassa.categoryCreated'));
      }
      setOpen(false);
      form.resetFields();
    } catch (err) {
      if ((err as { errorFields?: unknown[] }).errorFields) return;
      void message.error(asApiError(err).message);
    }
  }

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography.Title level={3} style={{ margin: 0 }}>{t('kassa.categories')}</Typography.Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>{t('kassa.newCategory')}</Button>
      </div>

      <Segmented
        value={scopeFilter}
        onChange={(v) => setScopeFilter(v as 'all' | 'income' | 'expense')}
        options={[
          { value: 'all', label: t('kassa.scopeAll') },
          { value: 'income', label: t('kassa.income') },
          { value: 'expense', label: t('kassa.expense') },
        ]}
      />

      <Card bodyStyle={{ padding: 0 }}>
        <Table<CashCategory>
          rowKey="id"
          loading={isLoading}
          dataSource={filtered}
          pagination={false}
          columns={[
            { title: t('kassa.categoryName'), dataIndex: 'name', render: (v: string, r: CashCategory) => <Space>{v}{r.isSystem && <Tag color="blue">{t('kassa.system')}</Tag>}{!r.active && <Tag>{t('users.inactive')}</Tag>}</Space> },
            { title: t('kassa.categoryScope'), dataIndex: 'scope', width: 140, render: (s: CashCategoryScope) => t(`kassa.scopes.${s}`) },
            { title: t('kassa.requiresCounterparty'), dataIndex: 'requiresCounterparty', width: 160, render: (v: boolean) => (v ? t('common.yes') : t('common.no')) },
            {
              title: '',
              width: 100,
              render: (_: unknown, r: CashCategory) => (
                <Space size={4}>
                  <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
                  {!r.isSystem && (
                    <Popconfirm
                      title={t('kassa.categoryDeleteConfirm')}
                      okText={t('common.delete')}
                      okButtonProps={{ danger: true }}
                      cancelText={t('common.cancel')}
                      onConfirm={() => {
                        void remove.mutateAsync(r.id)
                          .then(() => void message.success(t('kassa.categoryDeleted')))
                          .catch((err) => void message.error(asApiError(err).message));
                      }}
                    >
                      <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  )}
                </Space>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title={editing ? t('kassa.editCategory') : t('kassa.newCategory')}
        open={open}
        onOk={onOk}
        onCancel={() => { setOpen(false); form.resetFields(); }}
        confirmLoading={create.isPending || update.isPending}
        okText={editing ? t('common.save') : t('common.create')}
        cancelText={t('common.cancel')}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" requiredMark={false} initialValues={{ scope: 'both', requiresCounterparty: false, sortOrder: 0, active: true }}>
          <Form.Item name="name" label={t('kassa.categoryName')} rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="scope" label={t('kassa.categoryScope')}>
            <Select options={SCOPES.map((s) => ({ value: s, label: t(`kassa.scopes.${s}`) }))} />
          </Form.Item>
          <Form.Item name="requiresCounterparty" label={t('kassa.requiresCounterparty')}>
            <Select options={[{ value: true, label: t('common.yes') }, { value: false, label: t('common.no') }]} />
          </Form.Item>
          <Form.Item name="sortOrder" label={t('kassa.sortOrder')}><InputNumber style={{ width: '100%' }} min={0} max={9999} /></Form.Item>
          <Form.Item name="active" label={t('users.status')}>
            <Select options={[{ value: true, label: t('users.active') }, { value: false, label: t('users.inactive') }]} />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
